import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetLedgerTrialBalance,
  useGetLedgerAccountEntries,
  useGetLedgerApByVendor,
  useGetLedgerPnl,
  useListLedgerAccounts,
  useListPostingExceptions,
  useResolvePostingException,
  useCreatePartnerReimbursement,
  useListLedgerPaymentAccounts,
  useGetLedgerConfig,
  useUpdateLedgerConfig,
  useListUsers,
  useUpdateUser,
  getListPostingExceptionsQueryKey,
  getGetLedgerAccountEntriesQueryKey,
  getListUsersQueryKey,
} from "@workspace/api-client-react";
import type {
  GetLedgerAccountEntriesParams,
  LedgerLine,
  PnlGroupBy,
  PostingException,
  User,
} from "@workspace/api-client-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth-context";
import { inr, today } from "@/lib/format";
import { MasterDataPanel } from "@/components/ledger/master-data-panel";
import { BackfillTab } from "@/components/ledger/backfill-tab";
import { TriangleAlert, Info } from "lucide-react";

// ── Divergent wire shapes ───────────────────────────────────────────────
// 57-09's generated OpenAPI/zod contract for these five derived-read
// endpoints is documented (57-13-SUMMARY.md, 57-16-SUMMARY.md) as narrower
// than what ledger-journal.ts actually returns. Local types + a boundary
// cast, rather than a hand-written second fetch, per this plan's own rule.
interface TrialBalanceRowReal {
  accountCode: string;
  accountName: string;
  type: string;
  debit: number;
  credit: number;
  balance: number;
}
interface TrialBalanceResponseReal {
  rows: TrialBalanceRowReal[];
  totalDebit: number;
  totalCredit: number;
  balanced: boolean;
}
interface ApVendorRowReal {
  vendorId: number;
  vendorName: string;
  glOutstanding: number;
  subLedgerOutstanding: number;
  delta: number;
  awaitingPosting: number;
}
interface ApByVendorResponseReal {
  note: string;
  vendors: ApVendorRowReal[];
}
interface AccountEntryRowReal extends LedgerLine {
  entryDate: string;
  entryNo: string;
  runningBalance: number;
}
interface PaginatedAccountEntries {
  items: AccountEntryRowReal[];
  total: number;
}

const PNL_GROUP_BY: { value: PnlGroupBy; label: string }[] = [
  { value: "cost_centre", label: "Cost centre" },
  { value: "location", label: "Location" },
  { value: "account", label: "Account" },
];

function defaultFrom(): string {
  const d = new Date();
  d.setUTCMonth(d.getUTCMonth() - 1);
  return d.toISOString().slice(0, 10);
}

// ── Trial balance ───────────────────────────────────────────────────────
function TrialBalanceTab() {
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(today());
  const query = useGetLedgerTrialBalance({ from, to });
  const data = query.data as unknown as TrialBalanceResponseReal | undefined;

  return (
    <div className="space-y-3">
      <div className="flex gap-3">
        <div>
          <label className="text-xs font-semibold">From</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold">To</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      {data && !data.balanced && (
        <Alert variant="destructive">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>Out of balance</AlertTitle>
          <AlertDescription>
            Total debits and total credits disagree. This should never happen — the database enforces it — so treat
            this as a genuine incident, not a rounding quirk.
          </AlertDescription>
        </Alert>
      )}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
                <TableHead className="text-right">Balance</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows ?? []).map((r) => (
                <TableRow key={r.accountCode}>
                  <TableCell className="text-sm">
                    {r.accountCode} · {r.accountName}
                  </TableCell>
                  <TableCell className="text-sm">{r.type}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{inr(r.debit)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{inr(r.credit)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{inr(r.balance)}</TableCell>
                </TableRow>
              ))}
              {data && (
                <TableRow className="font-semibold">
                  <TableCell colSpan={2}>Total</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{inr(data.totalDebit)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{inr(data.totalCredit)}</TableCell>
                  <TableCell />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Account ledger ──────────────────────────────────────────────────────
function AccountLedgerTab() {
  const { data: accounts } = useListLedgerAccounts({});
  const [code, setCode] = useState<string>("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const params = { from: from || undefined, to: to || undefined, pageSize: 200 } as unknown as GetLedgerAccountEntriesParams;
  const query = useGetLedgerAccountEntries(code, params, {
    query: { enabled: !!code, queryKey: getGetLedgerAccountEntriesQueryKey(code, params) },
  });
  const data = query.data as unknown as PaginatedAccountEntries | undefined;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label className="text-xs font-semibold">Account</label>
          <Select value={code} onValueChange={setCode}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Choose an account" />
            </SelectTrigger>
            <SelectContent>
              {(accounts ?? []).map((a) => (
                <SelectItem key={a.id} value={a.code}>
                  {a.code} · {a.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold">From</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold">To</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
      </div>
      {!code ? (
        <p className="text-sm text-muted-foreground">Choose an account to see its ledger.</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Entry</TableHead>
                  <TableHead>Cost centre</TableHead>
                  <TableHead className="text-right">Debit</TableHead>
                  <TableHead className="text-right">Credit</TableHead>
                  <TableHead className="text-right">Running balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data?.items ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="h-20 text-center text-muted-foreground">
                      No entries in range.
                    </TableCell>
                  </TableRow>
                ) : (
                  data!.items.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell className="text-sm">{l.entryDate.slice(0, 10)}</TableCell>
                      <TableCell className="text-sm">{l.entryNo}</TableCell>
                      <TableCell className="text-sm">{l.costCentreName}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{l.debit > 0 ? inr(l.debit) : ""}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{l.credit > 0 ? inr(l.credit) : ""}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{inr(l.runningBalance)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── AP by vendor ────────────────────────────────────────────────────────
function ApByVendorTab() {
  const query = useGetLedgerApByVendor({});
  const data = query.data as unknown as ApByVendorResponseReal | undefined;

  return (
    <div className="space-y-3">
      {data?.note && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertTitle>About the figures below</AlertTitle>
          <AlertDescription>{data.note}</AlertDescription>
        </Alert>
      )}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vendor</TableHead>
                <TableHead className="text-right">GL outstanding</TableHead>
                <TableHead className="text-right">Sub-ledger outstanding</TableHead>
                <TableHead className="text-right">Delta</TableHead>
                <TableHead className="text-right">Awaiting posting</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.vendors ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-muted-foreground">
                    No open AP.
                  </TableCell>
                </TableRow>
              ) : (
                data!.vendors.map((v) => (
                  <TableRow key={v.vendorId}>
                    <TableCell className="text-sm">{v.vendorName}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{inr(v.glOutstanding)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{inr(v.subLedgerOutstanding)}</TableCell>
                    <TableCell className={`text-right font-mono tabular-nums ${Math.abs(v.delta) > 0.01 ? "text-destructive" : ""}`}>
                      {inr(v.delta)}
                    </TableCell>
                    <TableCell className="text-right">{v.awaitingPosting > 0 ? v.awaitingPosting : "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── P&L ─────────────────────────────────────────────────────────────────
function PnlTab() {
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(today());
  const [groupBy, setGroupBy] = useState<PnlGroupBy>("cost_centre");
  const { data } = useGetLedgerPnl({ from, to, groupBy });

  return (
    <div className="space-y-3">
      <div className="flex gap-3 items-end">
        <div>
          <label className="text-xs font-semibold">From</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold">To</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div>
          <label className="text-xs font-semibold">Group by</label>
          <Select value={groupBy} onValueChange={(v) => setGroupBy(v as PnlGroupBy)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PNL_GROUP_BY.map((g) => (
                <SelectItem key={g.value} value={g.value}>
                  {g.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{PNL_GROUP_BY.find((g) => g.value === groupBy)?.label}</TableHead>
                <TableHead className="text-right">Income</TableHead>
                <TableHead className="text-right">Expense</TableHead>
                <TableHead className="text-right">Net</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data?.rows ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="h-20 text-center text-muted-foreground">
                    No activity in range.
                  </TableCell>
                </TableRow>
              ) : (
                data!.rows.map((r) => (
                  <TableRow key={r.groupKey}>
                    <TableCell className="text-sm">{r.groupLabel}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{inr(r.income)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{inr(r.expense)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums">{inr(r.net)}</TableCell>
                  </TableRow>
                ))
              )}
              {data && (
                <TableRow className="font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{inr(data.totalIncome)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{inr(data.totalExpense)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{inr(data.net)}</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Quarantine ──────────────────────────────────────────────────────────
function QuarantineTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [showResolved, setShowResolved] = useState(false);
  const { data: exceptions, isLoading } = useListPostingExceptions({ resolved: showResolved });
  const resolveMut = useResolvePostingException();
  const [target, setTarget] = useState<PostingException | null>(null);
  const [note, setNote] = useState("");

  async function submitResolve() {
    if (!target || !note.trim()) {
      toast({ title: "A note is required.", variant: "destructive" });
      return;
    }
    try {
      await resolveMut.mutateAsync({ id: target.id, data: { note: note.trim() } });
      qc.invalidateQueries({ queryKey: getListPostingExceptionsQueryKey() });
      toast({ title: "Marked resolved." });
      setTarget(null);
      setNote("");
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not resolve.", variant: "destructive" });
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <Button size="sm" variant={showResolved ? "outline" : "default"} onClick={() => setShowResolved(false)}>
          Unresolved
        </Button>
        <Button size="sm" variant={showResolved ? "default" : "outline"} onClick={() => setShowResolved(true)}>
          Resolved
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Event type</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : (exceptions ?? []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="h-20 text-center text-muted-foreground">
                    {showResolved ? "Nothing resolved yet." : "Nothing quarantined — every event has a matching posting rule."}
                  </TableCell>
                </TableRow>
              ) : (
                exceptions!.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="text-sm">{new Date(e.createdAt).toLocaleString("en-IN")}</TableCell>
                    <TableCell className="text-sm">{e.eventType}</TableCell>
                    <TableCell className="text-sm">
                      {e.sourceModule} #{e.sourceEventId ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm">{e.reason}</TableCell>
                    <TableCell className="text-right">
                      {!e.resolvedAt && (
                        <Button size="sm" onClick={() => setTarget(e)}>
                          Resolve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={target != null} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Resolve quarantined event #{target?.id}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This marks the exception resolved — it does not re-post anything. Re-run the originating action (e.g.
            re-approve, or the backfill run) to actually post it.
          </p>
          <div>
            <label className="text-xs font-semibold">
              Note <span className="text-destructive">*</span>
            </label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>
              Cancel
            </Button>
            <Button onClick={submitResolve} disabled={resolveMut.isPending}>
              {resolveMut.isPending ? "Saving…" : "Mark resolved"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Partner Current + Reimburse (A1) ──────────────────────────────────────
const PARTNER_CURRENT_CODE = "30200";

function PartnerCurrentCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const params = { pageSize: 200 } as unknown as GetLedgerAccountEntriesParams;
  const query = useGetLedgerAccountEntries(PARTNER_CURRENT_CODE, params);
  const data = query.data as unknown as PaginatedAccountEntries | undefined;
  // The account ledger is sorted ascending by date/entry/line (ledger-journal.ts) —
  // the last item's own running balance IS the current balance, provided the
  // full history fits in one page (accepted approximation, noted here).
  const balance = data && data.items.length > 0 ? data.items[data.items.length - 1]!.runningBalance : 0;

  const { data: paymentAccounts } = useListLedgerPaymentAccounts();
  const nonPartnerAccounts = (paymentAccounts ?? []).filter((a) => a.paymentSourceKind !== "partner");
  const reimburseMut = useCreatePartnerReimbursement();

  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(today());
  const [paymentAccountId, setPaymentAccountId] = useState("");

  async function submit() {
    if (!amount || Number(amount) <= 0 || !paymentAccountId) {
      toast({ title: "Amount and a funding account are both required.", variant: "destructive" });
      return;
    }
    try {
      await reimburseMut.mutateAsync({
        data: { amount: Number(amount), paymentDate, paymentAccountId: Number(paymentAccountId) },
      });
      qc.invalidateQueries({ queryKey: getGetLedgerAccountEntriesQueryKey(PARTNER_CURRENT_CODE) });
      toast({ title: "Partner reimbursed." });
      setOpen(false);
      setAmount("");
      setPaymentAccountId("");
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not reimburse.", variant: "destructive" });
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground">Partner Current Account (30200)</CardTitle>
      </CardHeader>
      <CardContent className="flex items-center justify-between">
        <span className="text-2xl font-semibold font-mono tabular-nums">{inr(balance)}</span>
        <Button size="sm" onClick={() => setOpen(true)} disabled={balance <= 0}>
          Reimburse
        </Button>
      </CardContent>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reimburse partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Amount ₹</label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <p className="text-[11px] text-muted-foreground mt-0.5">Outstanding: {inr(balance)}</p>
            </div>
            <div>
              <label className="text-xs font-semibold">Date</label>
              <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
            </div>
            <div>
              <label className="text-xs font-semibold">Pay from</label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
                value={paymentAccountId}
                onChange={(e) => setPaymentAccountId(e.target.value)}
              >
                <option value="">Choose a funding account…</option>
                {nonPartnerAccounts.map((a) => (
                  <option key={a.id} value={String(a.id)}>
                    {a.code} · {a.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={reimburseMut.isPending}>
              {reimburseMut.isPending ? "Saving…" : "Reimburse"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ── Configuration (D9) ────────────────────────────────────────────────────
function ConfigurationCard() {
  const { toast } = useToast();
  const { data: config } = useGetLedgerConfig();
  const updateMut = useUpdateLedgerConfig();
  const [threshold, setThreshold] = useState<string>("");
  const [touched, setTouched] = useState(false);

  const displayValue = touched ? threshold : config?.billApprovalThreshold != null ? String(config.billApprovalThreshold) : "";

  async function save() {
    try {
      await updateMut.mutateAsync({ data: { billApprovalThreshold: displayValue === "" ? null : Number(displayValue) } });
      toast({ title: "Threshold updated." });
      setTouched(false);
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not update.", variant: "destructive" });
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground">Configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <label className="text-xs font-semibold">Bill approval threshold ₹</label>
        <Input
          type="number"
          step="0.01"
          placeholder="Leave blank to require super-admin for every bill"
          value={displayValue}
          onChange={(e) => {
            setTouched(true);
            setThreshold(e.target.value);
          }}
        />
        <p className="text-[11px] text-muted-foreground">
          Bills above this amount require super-admin approval. Leave blank to require super-admin approval for
          every bill.
        </p>
        <Button size="sm" onClick={save} disabled={updateMut.isPending}>
          {updateMut.isPending ? "Saving…" : "Save"}
        </Button>
      </CardContent>
    </Card>
  );
}

// ── Bill approval delegates (D9 gap closure) ──────────────────────────────
// 57-16 built the space-level threshold above but logged (deferred-items.md,
// "57-16: the per-user can_approve_bills toggle has no backing API anywhere
// in the generated contract") that the OTHER half of D9 — WHO is delegated
// to approve below that threshold — had no UI, and could only be set with a
// direct SQL UPDATE. This card is that missing surface: PATCH /users/:id
// now accepts `canApproveBills`, gated by the same requireAdmin this route
// already enforced. super_admin rows are excluded from the list below —
// they can always approve, any amount, regardless of this flag
// (57-ROLE-MAPPING.md), so toggling it for them would be a no-op.
function ApprovalDelegatesCard() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: users, isLoading } = useListUsers();
  const updateUser = useUpdateUser();

  const delegates = (users ?? []).filter((u) => u.role !== "super_admin");

  async function toggle(u: User) {
    try {
      await updateUser.mutateAsync({ id: u.id, data: { canApproveBills: !u.canApproveBills } });
      qc.invalidateQueries({ queryKey: getListUsersQueryKey() });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not update.", variant: "destructive" });
    }
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm text-muted-foreground">Bill approval delegates</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-[11px] text-muted-foreground">
          Below the threshold above, a bill can be approved by a super-admin (always) or by anyone toggled on here.
          This is the only way to grant or revoke that right for a specific person.
        </p>
        <div className="divide-y">
          {isLoading ? (
            <p className="text-sm text-muted-foreground py-3">Loading…</p>
          ) : delegates.length === 0 ? (
            <p className="text-sm text-muted-foreground py-3">No team members to configure.</p>
          ) : (
            delegates.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">{u.name}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {u.role}
                    {!u.active ? " · disabled" : ""}
                  </p>
                </div>
                <Switch
                  checked={u.canApproveBills}
                  onCheckedChange={() => toggle(u)}
                  disabled={updateUser.isPending}
                />
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function Ledger() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [tab, setTab] = useState("trial-balance");

  const tabs = useMemo(() => {
    const base = [
      { value: "trial-balance", label: "Trial Balance" },
      { value: "account-ledger", label: "Account Ledger" },
      { value: "ap-by-vendor", label: "AP by Vendor" },
      { value: "pnl", label: "P&L" },
      { value: "backfill", label: "Backfill" },
      { value: "quarantine", label: "Quarantine" },
    ];
    if (isSuperAdmin) base.push({ value: "master-data", label: "Master data" });
    return base;
  }, [isSuperAdmin]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Ledger</h1>
        <p className="text-sm text-muted-foreground mt-1">
          The general ledger this space's books are actually derived from — every figure below is computed live from
          journal_line, never a stored balance.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <PartnerCurrentCard />
        {isSuperAdmin && <ConfigurationCard />}
        {isSuperAdmin && <ApprovalDelegatesCard />}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          {tabs.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="trial-balance">
          <TrialBalanceTab />
        </TabsContent>
        <TabsContent value="account-ledger">
          <AccountLedgerTab />
        </TabsContent>
        <TabsContent value="ap-by-vendor">
          <ApByVendorTab />
        </TabsContent>
        <TabsContent value="pnl">
          <PnlTab />
        </TabsContent>
        <TabsContent value="backfill">
          <BackfillTab />
        </TabsContent>
        <TabsContent value="quarantine">
          <QuarantineTab />
        </TabsContent>
        {isSuperAdmin && (
          <TabsContent value="master-data">
            <MasterDataPanel />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
